'use client'

import {
  getNormalizedAuthErrorName,
  normalizeAuthError,
} from '@/services/auth/normalizeAuthError'

const REFRESH_TOKEN_REVOKED_MESSAGE = 'Refresh Token has been revoked'
const USER_ALREADY_AUTHENTICATED_ERROR = 'UserAlreadyAuthenticatedException'
const UNEXPECTED_SIGN_IN_INTERRUPTION_NAME =
  'UnexpectedSignInInterruptionException'
const UNEXPECTED_SIGN_IN_INTERRUPTION_MESSAGE =
  'Unable to get user session following successful sign-in.'
const AUTO_SIGN_IN_UNAVAILABLE_NAME = 'AutoSignInException'
const AUTO_SIGN_IN_UNAVAILABLE_MESSAGE =
  'The autoSignIn flow has not started, or has been cancelled/completed.'
const SESSION_RECOVERY_ERROR_NAMES = [
  'NotAuthorizedException',
  'RefreshTokenReuseException',
  'TokenRevokedException',
] as const
const SESSION_RECOVERY_MESSAGE_PATTERNS = [
  /refresh token/i,
  /token.+revok/i,
  /session.+expired/i,
  /session.+invalid/i,
]

export function isRefreshTokenRevokedError(error: unknown): boolean {
  const { name, type, code, message } = normalizeAuthError(error)
  const normalizedName = name ?? type ?? code
  return (
    typeof normalizedName === 'string' &&
    normalizedName.startsWith('NotAuthorizedException') &&
    typeof message === 'string' &&
    message.toLowerCase().includes(REFRESH_TOKEN_REVOKED_MESSAGE.toLowerCase())
  )
}

export function isUserAlreadyAuthenticatedError(error: unknown): boolean {
  return getNormalizedAuthErrorName(error) === USER_ALREADY_AUTHENTICATED_ERROR
}

export function isUnexpectedSignInInterruptionError(error: unknown): boolean {
  const { name, type, code, message } = normalizeAuthError(error)
  const normalizedName = name ?? type ?? code
  return (
    normalizedName === UNEXPECTED_SIGN_IN_INTERRUPTION_NAME ||
    (typeof message === 'string' &&
      message.includes(UNEXPECTED_SIGN_IN_INTERRUPTION_MESSAGE))
  )
}

export function isAutoSignInUnavailableError(error: unknown): boolean {
  const { name, type, code, message } = normalizeAuthError(error)
  const normalizedName = name ?? type ?? code
  return (
    normalizedName === AUTO_SIGN_IN_UNAVAILABLE_NAME ||
    (typeof message === 'string' &&
      message.includes(AUTO_SIGN_IN_UNAVAILABLE_MESSAGE))
  )
}

export function isCognitoSessionRecoveryError(error: unknown): boolean {
  if (
    isRefreshTokenRevokedError(error) ||
    isUnexpectedSignInInterruptionError(error)
  ) {
    return true
  }

  const { name, type, code, message } = normalizeAuthError(error)
  const normalizedName = name ?? type ?? code
  if (typeof normalizedName !== 'string') return false

  if (
    SESSION_RECOVERY_ERROR_NAMES.some((errorName) =>
      normalizedName.startsWith(errorName),
    ) &&
    typeof message === 'string' &&
    SESSION_RECOVERY_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))
  ) {
    return true
  }

  return SESSION_RECOVERY_ERROR_NAMES.filter(
    (errorName) => errorName !== 'NotAuthorizedException',
  ).some((errorName) => normalizedName.startsWith(errorName))
}
